import { DimensionSide, ElementType, LineType, Point } from '@ritabygglov/core';
import { useCallback } from 'react';
import { UpdatePolylineCommand } from '../../../commands/UpdatePolylineCommand';
import { CommandManager } from '../../../commands/CommandManager';
import CanvasSession from '../../../services/CanvasSession';
import {
  AppState,
  SelectionCoordinates,
  SelectionMode,
  SelectionModes,
} from '../../../types';
import { extractSplitSegmentIndex } from '../utils/typeGuards';

const resolvePoint = (points: Point[], point: Point): Point =>
  point.connectingPointIndex !== undefined
    ? points[point.connectingPointIndex]
    : point;

/**
 * Split polyline segment in two A --> B, to A --> C --> B
 */
export const splitPolylineSegment = (
  points: Point[],
  segmentIndex: number
): Point[] => {
  if (segmentIndex < 0 || segmentIndex >= points.length - 1) return points;

  const start = resolvePoint(points, points[segmentIndex]);
  const end = resolvePoint(points, points[segmentIndex + 1]);

  const insertIndex = segmentIndex + 1;
  const newPoint: Point = {
    x: (start.x + end.x) / 2,
    y: (start.y + end.y) / 2,
    dimensionSide: points[segmentIndex].dimensionSide ?? DimensionSide.Below,
    cornerRadius: 0,
  };

  const newPoints = points.map((p) =>
    p.connectingPointIndex !== undefined &&
    p.connectingPointIndex >= insertIndex
      ? { ...p, connectingPointIndex: p.connectingPointIndex + 1 }
      : { ...p }
  );
  newPoints.splice(insertIndex, 0, newPoint);

  return newPoints;
};

export interface SplitSegmentDeps {
  appState: AppState;
  session: CanvasSession;
  commandManager: CommandManager;
  setSelectedElements: React.Dispatch<React.SetStateAction<string[]>>;
  setSelectionCoordinates: React.Dispatch<
    React.SetStateAction<SelectionCoordinates>
  >;
  setSelectionMode: React.Dispatch<React.SetStateAction<SelectionMode>>;
}

export const useSplitSegment = (hooks: SplitSegmentDeps) => {
  const {
    appState,
    session,
    commandManager,
    setSelectedElements,
    setSelectionCoordinates,
    setSelectionMode,
  } = hooks;

  const onStartSplitDrag = useCallback(
    (id: string) => {
      const elementId = id.split('-split')[0];
      const segmentIndex = extractSplitSegmentIndex(id);
      const element = appState.elements[elementId];
      if (
        !element ||
        element.type !== ElementType.Polyline ||
        element.lineType !== LineType.Line
      ) {
        return;
      }

      const { points } = element;
      const newPoints = splitPolylineSegment(points, segmentIndex);
      if (newPoints === points) return;

      const command = new UpdatePolylineCommand(
        element,
        { points: newPoints },
        session.currentUserId
      );
      commandManager.executeCommand(command);

      setSelectedElements([elementId]);
      setSelectionCoordinates((prev) => ({
        ...prev,
        selectedAnchor: segmentIndex + 1,
        originElements: [],
      }));
      setSelectionMode((prev) => ({
        ...prev,
        elementType: ElementType.Polyline,
        type: SelectionModes.Resizing,
      }));
    },
    [
      appState.elements,
      session.currentUserId,
      commandManager,
      setSelectedElements,
      setSelectionCoordinates,
      setSelectionMode,
    ]
  );

  return { onStartSplitDrag };
};
